// oxlint-disable-next-line no-restricted-imports -- the only permitted import of history; this module is the Ardoq-owned contract
import {
  Action,
  createBrowserHistory as createBrowserHistoryImplementation,
  createMemoryHistory as createMemoryHistoryImplementation,
  createPath as createPathImplementation,
  type History as HistoryImplementation,
  type Location as LocationImplementation,
  type To,
} from 'history';
import { withBasePath } from '../basePath';

export type Location = {
  pathname: string;
  search: string;
  hash: string;
  state: unknown;
};

export const createPath = ({
  pathname,
  search,
  hash,
}: Pick<Location, 'pathname' | 'search' | 'hash'>): string =>
  createPathImplementation({ pathname, search, hash });

export type HistoryAction = 'push' | 'replace' | 'traverse';

export type HistoryUpdate = {
  action: HistoryAction;
  location: Location;
};

export type HistoryPath = {
  pathname: string;
  search?: string;
  hash?: string;
};

export type HistoryDestination = string | HistoryPath;

export type History = {
  readonly location: Location;
  push: (to: HistoryDestination, state?: unknown) => void;
  replace: (to: HistoryDestination, state?: unknown) => void;
  go: (delta: number) => void;
  back: VoidFunction;
  forward: VoidFunction;
  listen: (listener: (update: HistoryUpdate) => void) => VoidFunction;
};

export type RouterHistory = Pick<History, 'location' | 'push' | 'replace'>;

export type CreateMemoryHistoryOptions = {
  initialEntries?: (string | (HistoryPath & { state?: unknown }))[];
  initialIndex?: number;
};

const appSegment = window.IS_PRESENTATION ? '/presentation' : '/app';
export const APPLICATION_BASE_PATH = withBasePath(appSegment);

const stripBasePath = (basePath: string, pathname: string): string => {
  if (pathname === basePath) return '/';
  if (pathname.startsWith(`${basePath}/`)) {
    return pathname.slice(basePath.length);
  }
  return pathname;
};

const addBasePath = (basePath: string, to: HistoryDestination): To => {
  if (typeof to === 'string') {
    return `${basePath}${to}`;
  }

  return {
    ...to,
    pathname: `${basePath}${to.pathname}`,
  };
};

const toLocation = (
  location: LocationImplementation,
  basePath: string
): Location => ({
  pathname: stripBasePath(basePath, location.pathname),
  search: location.search,
  hash: location.hash,
  state: location.state,
});

const toHistoryAction = (action: Action): HistoryAction => {
  if (action === Action.Push) return 'push';
  if (action === Action.Replace) return 'replace';
  return 'traverse';
};

type CreateHistoryOptions = {
  implementation: HistoryImplementation;
  basePath: string;
};

const createHistory = ({
  implementation,
  basePath,
}: CreateHistoryOptions): History => ({
  get location() {
    return toLocation(implementation.location, basePath);
  },
  push: (to, state) => implementation.push(addBasePath(basePath, to), state),
  replace: (to, state) =>
    implementation.replace(addBasePath(basePath, to), state),
  go: delta => implementation.go(delta),
  back: () => implementation.back(),
  forward: () => implementation.forward(),
  listen: listener =>
    implementation.listen(({ action, location }) =>
      listener({
        action: toHistoryAction(action),
        location: toLocation(location, basePath),
      })
    ),
});

export const createMemoryHistory = (
  options: CreateMemoryHistoryOptions = {}
): History =>
  createHistory({
    implementation: createMemoryHistoryImplementation(options),
    basePath: '',
  });

export const history: History = createHistory({
  implementation: createBrowserHistoryImplementation(),
  basePath: APPLICATION_BASE_PATH,
});
